import dayjs, { dateDisplayFormat } from '~libs/dayjs';

import { Column } from '~components/table-view';
import OpenRecordAction from '~support-at-home/components/config/service-cutover/OpenRecordAction';

/**
 * Step 3 of the service cutover assistant - the price plans carrying the services being cut over
 * across the cutover date.
 *
 * The revision number is shown because it is what a provider will look for afterwards: confirming
 * this step adds the next revision up, and that is the one the budget step moves budgets onto.
 *
 * Every column is given a width, and they total less than the modal's inner width. TableView lays
 * out on `auto` and its cells are `white-space: nowrap`, so a column left to size itself - the plan
 * name, which runs to "Personal Care Contribution Change - Impact Test" - makes the whole table
 * wider than its card and pushes Status off the edge behind a horizontal scrollbar. The name column
 * is the one allowed to wrap instead.
 */
const NAME_WIDTH = '230px';
const DATES_WIDTH = '165px';
const REVISION_WIDTH = '105px';
const PRICES_WIDTH = '70px';
const STATUS_WIDTH = '120px';
const OPEN_WIDTH = '60px';
const columns: Column[] = [
  {
    id: 'name',
    header: 'Price Plan',
    caption: 'The price plan carrying the services being cut over',
    type: 'text',
    style: { width: NAME_WIDTH },
    // Lands on the `td`, which is where the global `white-space: nowrap` sits - a class on the
    // inner cell wrapper would not reach it.
    classNames: { column: 'sah-cutover-wrap-cell' },
  },
  {
    id: 'dates',
    header: 'Plan Dates',
    caption: 'The period the plan itself is in effect for',
    type: 'text',
    style: { width: DATES_WIDTH },
    deriveValue: (row: any) => {
      const startDate = row.start_date ? dayjs(row.start_date).format(dateDisplayFormat) : 'N/A';
      const endDate = row.end_date ? dayjs(row.end_date).format(dateDisplayFormat) : 'N/A';
      return `${startDate} - ${endDate}`;
    },
  },
  {
    id: 'revision_sequence',
    header: 'Current Revision',
    caption: 'The latest revision of this plan, which the new one is copied from',
    type: 'text',
    style: { width: REVISION_WIDTH },
  },
  {
    id: 'price_count',
    header: 'Prices',
    caption: 'How many prices for the services being replaced will be carried across',
    type: 'text',
    style: { width: PRICES_WIDTH },
  },
  {
    id: 'status',
    header: 'Status',
    caption: 'Whether the latest revision already carries the replacement service',
    type: 'text',
    style: { width: STATUS_WIDTH },
    deriveValue: (row: any) => (row.already_carried ? 'Already carried' : 'To be carried'),
  },
  {
    id: 'open',
    header: '',
    caption: 'Open this price plan in a new tab',
    type: 'custom',
    style: { width: OPEN_WIDTH },
    renderReadOnly: (_value, row: any) =>
      OpenRecordAction({
        id: `cutover-open-price-plan-${row?.plan_id}`,
        url: `/support-at-home/configs?tab=price-plans&plan=${row?.plan_id}`,
        label: 'Open price plan',
      }),
  },
];

export default columns;
